import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { first, map } from 'rxjs/operators';

import { RootState } from '@root/app.state';
import { go } from '@platform/router.actions';
import { RouteGuard } from '@platform/routing/route.guard';
import * as CoreActions from '@core/core.actions';
import { selectLoadedMobileUpload } from '@core/core.selectors';
import { firstValueFrom } from 'rxjs';
import { ResourceStatuses } from '@platform/shared/resource.state';
import { MobileUploadDto } from '@core/mobile-upload/mobile-upload.model';
import { HttpStatusCode } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class MobileUploadGuard implements RouteGuard {
  constructor(
    private store$: Store<RootState>,
    private translate: TranslateService,
  ) {}

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean> {
    return await this.loadAndWaitForMobileUpload(route);
  }

  async canActivateChild(childRoute: ActivatedRouteSnapshot): Promise<boolean> {
    return await this.loadAndWaitForMobileUpload(childRoute.parent!);
  }

  private getLanguageFromRoute(route: ActivatedRouteSnapshot) {
    const langFromUrl = route.queryParamMap.get('lang');
    return langFromUrl === 'en-US' ? langFromUrl : 'de-CH';
  }

  private async loadAndWaitForMobileUpload(route: ActivatedRouteSnapshot): Promise<boolean> {
    const lang = this.getLanguageFromRoute(route);
    this.translate.use(lang);

    const customerSpaceId = route.paramMap.get('customerSpaceId')!;
    const mobileUploadId = route.paramMap.get('mobileUploadId')!;

    const mobileUploadDto = await this.getCurrentMobileUpload();
    if (!mobileUploadDto || mobileUploadDto.id !== mobileUploadId) {
      this.store$.dispatch(CoreActions.loadMobileUpload({ customerSpaceId, mobileUploadId }));
    }

    const mobileUploadState = await firstValueFrom(
      this.store$.pipe(
        select(selectLoadedMobileUpload),
        first((x) => x.status === ResourceStatuses.FAILED || x.status === ResourceStatuses.SUCCEEDED),
      ),
    );

    const canActivate = mobileUploadState.status === ResourceStatuses.SUCCEEDED;
    if (canActivate) {
      return true;
    }

    if (mobileUploadState.status === ResourceStatuses.FAILED && mobileUploadState.error?.status === HttpStatusCode.Gone) {
      this.store$.dispatch(go({ path: ['mobile-upload-expired'] }));
      return false;
    }

    return canActivate;
  }

  private async getCurrentMobileUpload(): Promise<MobileUploadDto> {
    return await firstValueFrom(
      this.store$.pipe(
        select(selectLoadedMobileUpload),
        first((state) => state.status !== ResourceStatuses.PENDING),
        map((state) => state.data),
      ),
    );
  }
}
